import {
  Box,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Stack,
  Typography,
} from "@mui/material";
import { Checkbox } from "@mui/material";
import { get } from "radash";
import React, { Fragment } from "react";
import { Controller, UseControllerProps, useFormContext } from "react-hook-form";

import FormFieldComponent from "@components/FormEngine/FormFieldComponent";
import { FormField } from "@components/FormEngine/types";
import { getFormErrorMessage } from "@components/PatientForms/common/schemaHelper";

import { getIsMobile } from "../../utils/helpers";

export type MultiSelectResponseOption = {
  value: string;
  label: string;
};

export type MultiSelectResponseProps = UseControllerProps<any> & {
  label: string;
  options: MultiSelectResponseOption[];
  helperText?: string;
  schema: FormField[]; // Schema for the conditional fields when an option is selected
  includeNoneOption?: boolean;
  noneOptionLabel?: string;
};

const MultiSelectResponse: React.FC<MultiSelectResponseProps> = ({
  control,
  name,
  label,
  options,
  schema,
  helperText,
  includeNoneOption = false,
  noneOptionLabel = "None of these",
}) => {
  const isMobile = getIsMobile();
  const { getValues } = useFormContext(); // retrieve all hook methods
  
  if (!control) return null;
  
  return (
    <Controller
    name={name}
    control={control}
    render={({ field, formState }) => {
      const error = getFormErrorMessage(formState, name)
      ? { message: getFormErrorMessage(formState, name) }
      : undefined;
      
      const value = getValues(name) || [];
      const isNoneSelected = value.some((item: any) => item.name === "None");
      
        const handleChange = (optionValue: string, checked: boolean) => {
          if (optionValue === "None") {
            if (checked) {
              // If "None" is selected, clear all other selections
              field.onChange([{ name: "None", description: "" }]);
            } else {
              // If "None" is unselected, just remove it
              field.onChange(value.filter((item: any) => item.name !== "None"));
            }
          } else {
            if (checked) {
              // If any other option is checked, make sure "None" is removed
              const filtered = value.filter((item: any) => item.name !== "None");
              field.onChange([...filtered, { name: optionValue, description: "" }]);
            } else {
              // Remove this item
              field.onChange(value.filter((item: any) => item.name !== optionValue));
            }
          }
        };

        return (
          <FormControl error={!!error} sx={{ width: "100%" }}>
            <FormLabel>{label}</FormLabel>
            <FormGroup sx={{ gap: { xs: 2, md: 1 } }}>
              {options.map((option) => {
                const index = value.findIndex((item: any) => item.name === option.value);
                const isSelected = index !== -1;

                return (
                  <Stack key={option.value}>
                    <Box sx={{ pt: isSelected ? 1 : 0, display: "flex" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size={isMobile ? "large" : "medium"}
                            key={`${name}.selected`}
                            name={`${name}.selected`}
                            checked={isSelected}
                            onChange={(e) => handleChange(option.value, e.target.checked)}
                            disabled={isNoneSelected}
                            sx={{ mr: 0, alignSelf: "flex-start" }}
                          />
                        }
                        label={isSelected ? "" : option.label}
                      />
                      {isSelected && (
                        <Card sx={{ backgroundColor: "background.default", width: "100%" }}>
                          <CardContent>
                            <Stack direction="column" spacing={1}>
                              <Typography variant="h6">{option.label}</Typography>
                              {schema.map((component) => {
                                const newComponentName = `${name}.${index}.${component.name}`;
                                return (
                                  <Fragment key={newComponentName}>
                                    <FormFieldComponent
                                      field={{ ...component, name: newComponentName }}
                                      control={control}
                                    />
                                  </Fragment>
                                );
                              })}
                            </Stack>
                          </CardContent>
                        </Card>
                      )}
                    </Box>
                  </Stack>
                );
              })}

              {includeNoneOption && (
                <Stack key="None">
                  <Box sx={{ display: "flex" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size={isMobile ? "large" : "medium"}
                          key={`${name}.None`}
                          name={`${name}.None`}
                          checked={isNoneSelected}
                          onChange={(e) => handleChange("None", e.target.checked)}
                          sx={{ mr: 0, alignSelf: "flex-start" }}
                        />
                      }
                      label={noneOptionLabel}
                    />
                  </Box>
                </Stack>
              )}
            </FormGroup>
            {(error?.message || helperText) && (
              <FormHelperText error={!!error}>{error?.message || helperText}</FormHelperText>
            )}
          </FormControl>
        );
      }}
    />
  );
};

export default MultiSelectResponse;
